import { addDays } from './analyticsUi';
import { buildView } from './profit/profitModel';
import { buildVerdict as buildConversionVerdict, pct1, rate } from './profit/conversionModel';
import { eur, ageLabel, horizonLabel, MIN_TABLE_USERS } from './profit/profitFormat';

// Plain-text reports of the Profit and Engagement tabs, made to be handed to an LLM: a guide first,
// then the same figures the tabs show, as small tables. The full JSON is exported next to it.

const n = (v, digits = 0) => (v == null || Number.isNaN(v) ? '–' : Number(v).toLocaleString('en-GB', { maximumFractionDigits: digits }));
const pc = (v) => (v == null || Number.isNaN(v) ? '–' : `${Math.round(v * 100)}%`);
const money = (v) => (v == null ? '–' : eur(v, true));
const cell = (v) => String(v ?? '–').replace(/\|/g, '/').replace(/\n/g, ' ');

function table(headers, rows) {
  if (!rows.length) return '_No data._\n';
  return [
    `| ${headers.join(' | ')} |`,
    `|${headers.map(() => '---').join('|')}|`,
    ...rows.map(r => `| ${r.map(cell).join(' | ')} |`),
    '',
  ].join('\n');
}

const section = (title, body, hint) => `## ${title}\n${hint ? `_${hint}_\n` : ''}\n${body}\n`;
const bullets = (items) => `${items.filter(Boolean).map(i => `- ${i}`).join('\n')}\n`;
const stamp = (iso) => (iso ? iso.slice(0, 16).replace('T', ' ') + ' UTC' : 'unknown');

function warningsBlock(data, extra) {
  const list = [...(extra ? [extra] : []), ...(data?.warnings || [])];
  return list.length ? section('Data warnings', bullets(list), 'Numbers below may be incomplete because of these.') : '';
}

function verdictBlock(v) {
  if (!v) return '';
  return `**${v.title}.** ${v.text}\n\n${bullets((v.figures || []).map(f => `${f.label}: **${f.value}**${f.note ? ` (${f.note})` : ''}`))}`;
}

const arm = (a) => (a ? `${n(a.buyers)}/${n(a.viewers)} (${pct1(rate(a.buyers, a.viewers))})` : '–');
const perViewer = (trials, all) => (trials && all?.viewers ? money(trials.net / all.viewers) : '–');
const perDay = (v) => n(v, 1);

// The Ad planning tab's model results: they are worked out in the browser, so the JSON doesn't have them
function budgetDetails(budget) {
  const { test, marginal, curve, limit, periods, steps, current, payback, ads } = budget;
  const out = [];
  if (test) {
    const { from, to } = test;
    let result = 'Still settling in, nothing measured yet.';
    if (test.measuring) {
      if (test.costRange) result = `Extra users cost ${money(test.costRange.low)}–${money(test.costRange.high)} each, against a limit of ${money(limit)}.`;
      else if (test.clear) result = to.budget > from.budget ? 'Fewer users despite more spend.' : 'More users despite less spend.';
      else result = 'No clear change in new users yet: the difference is within normal ups and downs.';
    }
    out.push(section(
      `Budget test: ${money(from.budget)} → ${money(to.budget)} since ${to.from}`,
      `${test.done ? 'Finished.' : `Day ${test.daysSoFar} of ${test.needed}.`} ${result}\n\n` +
        (test.measuring
          ? bullets([
            `New users per day: ${perDay(from.signupsPerDay)} → ${perDay(to.signupsPerDay)} (90% range of the change: ${perDay(test.lowUsers)} to ${perDay(test.highUsers)})`,
            `Spend per day: ${money(from.spendPerDay)} → ${money(to.spendPerDay)}`,
            `Credited installs per day: ${perDay(from.creditedPerDay)} → ${perDay(to.creditedPerDay)}`,
          ])
          : ''),
      `Measured from ${to.measuredFrom}, after Google has had time to adjust.`,
    ));
  }
  if (ads?.networkShift) {
    out.push(section(
      'Where the budget test\'s money went, per day',
      table(['Network', 'Spend before', 'Spend now', 'Conversions before', 'Conversions now', 'Cost per extra conversion'], ads.networkShift.rows.map(r => [r.network, money(r.spendBefore), money(r.spendAfter), perDay(r.conversionsBefore), perDay(r.conversionsAfter), money(r.costPerExtra)])),
      `From ${ads.networkShift.daysBefore} days before and ${ads.networkShift.daysAfter} days since the change that Google has fully split.`,
    ));
  }
  const finite = (v) => (Number.isFinite(v) ? money(v) : 'no effect');
  out.push(section(
    'What the budget model says',
    bullets([
      `Worth paying per extra user: up to ${money(limit)}`,
      marginal && Number.isFinite(marginal.value) && `Cost of one more user at today's spend: ${money(marginal.value)} (likely ${finite(marginal.low)} to ${finite(marginal.high)})`,
      curve.fit
        ? `Best daily budget: ${money(curve.best.value)}${curve.best.range ? ` (likely ${money(curve.best.range.low)}–${money(curve.best.range.high)})` : ''}, from ${n(curve.weeks)} weeks of data. Weekly spend so far has been ${money(curve.tested.low)}–${money(curve.tested.high)} a day; beyond that it is a guess`
        : `No budget curve: ${curve.reason}`,
      curve.adsShare?.range && `Share of new users that come from ads: ${pc(curve.adsShare.value)} (likely ${pc(curve.adsShare.range.low)}–${pc(curve.adsShare.range.high)})`,
      ...payback.map(p => `Payback, ${p.label.toLowerCase()} (${money(p.cost)}): ${p.age != null ? `${p.extrapolated ? 'about ' : ''}${ageLabel(p.age)}` : 'not within the data'}`),
    ]),
    'Worked out from all new users, not only those Google credits.',
  ));
  if (periods.length || current) {
    const rows = periods.map(p => {
      const step = steps.find(s => s.to === p);
      let cost = '–';
      if (step) cost = !step.measurable ? 'unclear' : step.cost != null ? money(step.cost) : (step.dSpend > 0 ? 'fewer users' : 'more users');
      return [`${p.from} – ${p.to}`, money(p.budget), money(p.spendPerDay), perDay(p.signupsPerDay), perDay(p.creditedPerDay), cost];
    });
    if (current) rows.push([`${current.from} – now`, money(current.budget), 'too new', '–', '–', '–']);
    out.push(section(
      'Budget history',
      table(['Period', 'Budget', 'Spend per day', 'New users per day', 'Credited installs per day', 'Cost of each extra user'], rows),
      'The first days after each change are left out.',
    ));
  }
  return out.join('\n');
}

function conversionSections(c) {
  if (!c) return '_No conversion data yet._\n';
  const { flow, paywall } = c.later;
  const out = [];
  out.push(section(
    `Which paywall converts more? (${c.since} – ${c.until})`,
    `${verdictBlock(buildConversionVerdict(c))}
` +
      table(
        ['Paywall', 'Users who saw it', 'Started a subscription', 'Rate'],
        [['Multi-step', flow.viewers, flow.buyers, pct1(rate(flow.buyers, flow.viewers))], ['Single screen', paywall.viewers, paywall.buyers, pct1(rate(paywall.buyers, paywall.viewers))]],
      ),
    'Rates are for users after their first look (the "later" group). The app alternates paywalls rather than randomising, so treat gaps as hints. Cells below are buyers/viewers (rate).',
  ));
  if (c.turns?.length) {
    out.push(section('By turn (which time the user saw a paywall)', table(['Turn', 'Multi-step', 'Single screen'], c.turns.map(t => [t.turn, arm(t.flow), arm(t.paywall)]))));
  }
  if (c.steps) {
    const stepKeys = ['viewed', 'comparison', 'plan', 'attempted', 'purchased'];
    out.push(section(
      'Paywall funnel (users reaching each step)',
      table(['Paywall', ...stepKeys], [['Multi-step', ...stepKeys.map(k => c.steps.flow?.[k])], ['Single screen', ...stepKeys.map(k => c.steps.paywall?.[k])]]),
      'Single screen has no comparison or plan steps.',
    ));
  }
  const bits = [];
  if (c.plans) bits.push(table(['Plan chosen', 'Multi-step', 'Single screen'], [['Yearly', c.plans.flow.yearly, c.plans.paywall.yearly], ['Monthly', c.plans.flow.monthly, c.plans.paywall.monthly]]));
  if (c.trials) {
    const t = (x) => x || {};
    bits.push(table(['Trials', 'Multi-step', 'Single screen'], [
      ['Started', t(c.trials.flow).trials, t(c.trials.paywall).trials],
      ['Settled (trial ended)', t(c.trials.flow).settled, t(c.trials.paywall).settled],
      ['Paid after trial', t(c.trials.flow).paid, t(c.trials.paywall).paid],
      ['Net revenue so far', money(t(c.trials.flow).net), money(t(c.trials.paywall).net)],
      ['Net revenue per user who saw it', perViewer(c.trials.flow, c.all?.flow), perViewer(c.trials.paywall, c.all?.paywall)],
    ]));
  }
  if (bits.length) out.push(section('What the subscriptions are worth', bits.join('\n'), 'Trials count as ended 8 days after they start. Multi-step shows every first offer and single screen does not, so revenue per user carries that bias.'));
  if (c.failures) {
    const reasons = [...new Set([...Object.keys(c.failures.flow || {}), ...Object.keys(c.failures.paywall || {})])];
    out.push(section('Purchase failures', table(['Reason', 'Multi-step', 'Single screen'], reasons.map(r => [r, c.failures.flow?.[r] || 0, c.failures.paywall?.[r] || 0]))));
  }
  if (c.sources?.length) {
    out.push(section('Where the paywall was opened from', table(['Source', 'All paywalls', 'Multi-step', 'Single screen'], c.sources.map(x => [x.source, arm(x.all), arm(x.flow), arm(x.paywall)]))));
  }
  if (c.other?.length) {
    out.push(section('Other offers (not part of the comparison)', table(['Offer', 'Multi-step', 'Single screen'], c.other.map(o => [o.label, o.flow.views ? arm(o.flow) : '–', o.paywall.views ? arm(o.paywall) : '–']))));
  }
  return out.join('\n');
}

export function buildProfitReport(data, markers = []) {
  const view = buildView(data, 90, markers);
  const lines = [];
  lines.push('# Norppa profit report');
  lines.push(`Data updated: ${stamp(data?.lastUpdated)}. All money in EUR; subscription revenue is after Google's cut and tax.\n`);
  lines.push(section('How to use this file (for an LLM)', bullets([
    'This is a summary of the admin panel\'s Profit tab (Earnings, Ad planning and Conversion sub-tabs). Everything needed for normal questions is here, so you do not need the JSON.',
    '"Cash profit" = revenue minus ad spend in the period. "Expected profit" = what a month\'s new users are projected to earn over the decision horizon minus that month\'s spend (projected from how older signups grew).',
    '"Cost per user" is a range: all new users (best case) to only users Google credits to ads (worst case).',
    'Rows marked "projected" or "≈" are estimates, not observed money.',
    'The companion file `norppa-profit-<date>.json` has the raw data (daily rows, signup days, campaigns, ads budget). Only open it for questions this file cannot answer, and read only the keys you need: `daily[]` (date, spend, installs, installsPaid, signups, subRevenue, adRevenue), `monthly[]`, `cohorts[]`, `signupDays[]`, `campaigns[]`, `adsBudget`, `conversion`, `health`, `markers[]`.',
  ])));

  if (!view) {
    lines.push('_No analytics data yet._\n');
    lines.push(warningsBlock(data));
    return lines.join('\n');
  }

  const { last30, prev30, paidShare, typical, returnAge, cohortRows, expectedNow, expectedPrev, expectedRange, accuracy, forecast, budget, today } = view;
  const costLow = last30.costPerUser;
  const costHigh = costLow && paidShare > 0 && paidShare < 1 ? costLow / paidShare : null;
  const horizon = returnAge != null ? horizonLabel(returnAge) : null;

  const latest = data.daily[data.daily.length - 1];
  if (latest && !latest.spend && !latest.installs && latest.signups) {
    lines.push(`> **Note:** the newest day (${latest.date}) is incomplete: no spend or installs yet, but ${latest.signups} signups. It is still counted in the last-30-day figures, so cost per new user is slightly understated.
`);
  }
  lines.push(warningsBlock(data));
  lines.push(section('Verdict', verdictBlock(view.verdict)));

  const row = (label, a, b, fmt) => [label, fmt(a), fmt(b)];
  lines.push(section(
    'Last 30 days vs the 30 before',
    table(['', `Last 30 days (to ${today})`, 'Previous 30 days'], [
      row('Ad spend', last30.spend, prev30.spend, money),
      row('Installs', last30.installs, prev30.installs, v => n(v)),
      row('Installs credited to ads', last30.installsPaid, prev30.installsPaid, v => n(v)),
      row('New users (signups)', last30.signups, prev30.signups, v => n(v)),
      row('Cost per new user', last30.costPerUser, prev30.costPerUser, money),
      row('Subscription revenue', last30.subRevenue, prev30.subRevenue, money),
      row('Ad revenue', last30.adRevenue, prev30.adRevenue, money),
      row('Cash profit', last30.profit, prev30.profit, money),
      ['Expected profit once new users have earned (' + (horizon || 'n/a') + ')', expectedNow ? money(expectedNow.profit) : '–', expectedPrev ? money(expectedPrev.profit) : '–'],
    ]) +
      bullets([
        paidShare != null && `Share of installs credited to ads: ${pc(paidShare)}`,
        costLow && `Ad cost per new user: ${money(costLow)}${costHigh ? ` (all new users) to ${money(costHigh)} (only credited users)` : ''}`,
        expectedRange && `Expected profit for the last 30 days, 90% range: ${money(expectedRange.low)} to ${money(expectedRange.high)}`,
        accuracy && `Projection backtest: mean absolute error ${pc(accuracy.meanAbsError)} on 90-day earnings per user (${accuracy.rows.map(r => `cutoff ${r.cutoff}: predicted ${money(r.predicted)} vs actual ${money(r.actual)}`).join('; ')})`,
      ]),
  ));

  lines.push(section(
    'Earnings per typical new user, by age',
    table(['Age since signup', 'Cumulative earnings', 'Observed or extrapolated'], typical.map(p => [ageLabel(p.age), money(p.value), p.extrapolated ? 'extrapolated' : 'observed'])),
    data.renewal ? `Yearly renewals modelled: ${money(data.renewal.perUser)} per user from day ${data.renewal.age}, repeat rate ${pc(data.renewal.rate)}.` : undefined,
  ));

  const monthly = (data.monthly || []).slice(-18);
  lines.push(section(
    'Money per month',
    table(['Month', 'Installs', 'New users', 'Spend', 'Subscriptions', 'Ad revenue', 'Cash profit'], monthly.map(m => [m.month, n(m.installs), n(m.signups), money(m.spend), money(m.subRevenue), money(m.adRevenue), money(m.profit)])),
    'Last 18 months.',
  ));

  lines.push(section(
    'Signup months (cohorts)',
    table(
      ['Month', 'Users', 'Spend', 'Cost per user', ...(returnAge != null ? [`Return per €1 at ${ageLabel(returnAge)}`] : []), 'Pays back at', 'Expected profit'],
      cohortRows.filter(c => c.users >= MIN_TABLE_USERS).map(c => [
        c.month, n(c.users), money(c.spend), money(c.cac),
        ...(returnAge != null ? [c.return ? `${c.return.value.toFixed(2)}${c.return.projected ? ' (projected)' : ''}` : '–'] : []),
        c.payback ? `${ageLabel(c.payback.age)}${c.payback.projected ? ' (projected)' : ''}` : 'not yet',
        c.expectedProfit != null ? `≈ ${money(c.expectedProfit)}` : '–',
      ]),
    ),
    `Newest first; months with fewer than ${MIN_TABLE_USERS} users are left out.`,
  ));

  if (forecast) {
    lines.push(section(
      'Cash forecast, ads as now',
      table(['Month', 'From existing users', 'From new users', 'Ad spend', 'Profit if ads continue', 'Profit if ads stop'], forecast.rows.map(r => [r.month, money(r.existing), money(r.future), money(r.spend), money(r.keep), money(r.stop)])) +
        `Assumes ${n(forecast.signupsPerDay, 1)} signups and ${money(forecast.spendPerDay)} spend per day (${forecast.paceFrom ? `the pace since ${forecast.paceFrom}, the current budget` : "last 30 days' pace"}).\n`,
    ));
  }

  if (budget) {
    const ab = data.adsBudget || {};
    const spendByDate = Object.fromEntries(data.daily.map(d => [d.date, d.spend]));
    const budgetByDate = Object.fromEntries((ab.daily || []).map(d => [d.date, d.budget]));
    const delivery = (ab.delivery || []).slice(-14);
    lines.push(section(
      'Ad planning',
      `**${budget.title}.** ${budget.text}

` + bullets([
        budget.spendNow != null && `Spend now: ${money(budget.spendNow)} per day`,
        budget.budgetNow != null && `Daily budget now: ${money(budget.budgetNow)}`,
        budget.usedShare != null && `Share of budget actually spent: ${pc(budget.usedShare)}`,
        budget.ads?.bidTarget ? `Bid target: ${money(budget.ads.bidTarget.target)} per install, an average rather than a cap on each install${budget.ads.bidTarget.since ? ` (since ${budget.ads.bidTarget.since})` : ''}` : 'Bid target: none recorded',
        budget.ads?.now?.costPerConversion != null && `Google's cost per conversion, last 14 days: ${money(budget.ads.now.costPerConversion)}${budget.ads.prev?.costPerConversion != null ? ` (14 days before: ${money(budget.ads.prev.costPerConversion)})` : ''}`,
        ab.biddingStrategies?.length && `Bidding strategy: ${ab.biddingStrategies.join(', ')}`,
      ]) +
        (budget.ads?.networks?.length ? '\nSpend by Google network, last 30 complete days (conversions include in-app purchases; "from views" means the user did not click):\n\n' + table(['Network', 'Spend', 'Impressions', 'Clicks', 'Conversions', 'From views', 'Cost per conversion'], budget.ads.networks.map(x => [x.network, money(x.spend), n(x.impressions), n(x.clicks), n(x.conversions), n(x.viewThrough), x.conversions ? money(x.spend / x.conversions) : '–'])) : '') +
        (delivery.length ? '\nDelivery, last 14 days:\n\n' + table(['Date', 'Budget', 'Spend', 'Impressions', 'Clicks', 'Conversions'], delivery.map(d => [d.date, money(budgetByDate[d.date]), money(spendByDate[d.date]), n(d.impressions), n(d.clicks), n(d.conversions)])) : ''),
      'The Ad planning sub-tab.',
    ));
    lines.push(budgetDetails(budget));
  }

  if (data.campaigns?.length) {
    lines.push(section(
      'Campaigns',
      table(['Campaign', 'Spend', 'Clicks', 'Installs credited', 'Users credited', 'Cost per user', 'Revenue so far', 'Return per €1'], data.campaigns.map(c => [c.name, money(c.spend), n(c.clicks), n(c.installs), n(c.users), money(c.costPerUser), money(c.revenue), c.roas != null ? c.roas.toFixed(2) : '–'])),
      `${data.campaignsSince ? `Since ${data.campaignsSince}, when campaign data per user starts. ` : ''}Only what Google ties to a campaign, and these users are still young, so revenue is a lower bound. Use the verdict for whether ads pay off.`,
    ));
  }

  const recent = data.daily.filter(d => d.date >= addDays(today, -29));
  lines.push(section(
    'Daily numbers, last 30 days',
    table(['Date', 'Spend', 'Installs', 'Credited to ads', 'Signups', 'Subscriptions', 'Ad revenue'], recent.map(d => [d.date, money(d.spend), n(d.installs), n(d.installsPaid), n(d.signups), money(d.subRevenue), money(d.adRevenue)])),
  ));

  if (markers.length) {
    lines.push(section('Markers (changes made to ad spend)', bullets(markers.map(m => `${m.date}: ${m.note}`))));
  }

  lines.push('# Conversion (the Conversion sub-tab)\n');
  lines.push(conversionSections(data.conversion));

  if (data.health) {
    lines.push(section('Data health', `\`\`\`json\n${JSON.stringify(data.health)}\n\`\`\`\n`));
  }
  return lines.join('\n');
}

// --- Engagement ---

const GAME_NAMES = { crossword: 'Crossword', wordle: 'Wordle', quordle: 'Neluilu', teralehti: 'Terälehti', sudoku: 'Sudoku' };
const gameName = (g) => GAME_NAMES[g] || g;
const share = (a, b) => (b ? pc(a / b) : '–');
const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);

// [{ value, users }] answer counts as a table with each answer's share
function answers(rows, label) {
  const list = rows || [];
  const total = sum(list, 'users');
  return table([label, 'Users', 'Share'], list.map(r => [r.value, n(r.users), share(r.users, total)]));
}

export function buildEngagementReport(data) {
  const lines = [];
  lines.push('# Norppa engagement report');
  lines.push(`Data updated: ${stamp(data?.lastUpdated)}. Today (Helsinki): ${data?.today || 'unknown'}.\n`);
  lines.push(section('How to use this file (for an LLM)', bullets([
    'This is a summary of the admin panel\'s Engagement tab. Everything needed for normal questions is here, so you do not need the JSON.',
    'Sources: GA4 (active users), Firestore (players per game, habit, retention, streaks, community puzzle stats), BigQuery Analytics export (game funnels, activation, paywall funnel, onboarding answers, play-time heatmap, platforms). The BigQuery events cover only the window shown in "Event window".',
    'DAU/WAU/MAU = daily/weekly/monthly active users. "Players" = users who finished or played at least one game that day.',
    'The companion file `norppa-engagement-<date>.json` has the raw data. Only open it for questions this file cannot answer, and read only the keys you need: `activity[]` (date, dau, wau, mau, installs), `players` (dailyPlayers[], habit, retention, streaks, onboarding), `puzzles`, `events` (games, crossword, paywall, onboarding, activation[], daily[], heatmap[], devices[]).',
  ])));
  if (!data) return `${lines.join('\n')}\n_No engagement data yet._\n`;
  lines.push(warningsBlock(data));

  const { activity = [], players, events } = data;
  if (activity.length) {
    const last = activity[activity.length - 1];
    const pick = (back) => activity[Math.max(0, activity.length - 1 - back)];
    lines.push(section(
      'Active users',
      bullets([
        `Latest (${last.date}): DAU ${n(last.dau)}, WAU ${n(last.wau)}, MAU ${n(last.mau)}. Stickiness DAU/MAU ${share(last.dau, last.mau)}`,
        `7 days ago: DAU ${n(pick(7).dau)}, MAU ${n(pick(7).mau)}; 30 days ago: DAU ${n(pick(30).dau)}, MAU ${n(pick(30).mau)}; 90 days ago: DAU ${n(pick(90).dau)}, MAU ${n(pick(90).mau)}`,
        `Installs last 30 days: ${n(sum(activity.slice(-30), 'installs'))}`,
      ]) + '\n' + table(['Date', 'DAU', 'WAU', 'MAU', 'Installs'], activity.slice(-14).map(a => [a.date, n(a.dau), n(a.wau), n(a.mau), n(a.installs)])),
      'Last 14 days shown; full history in the JSON under `activity`.',
    ));

    const byMonth = {};
    activity.forEach(a => {
      const m = a.date.slice(0, 7);
      const e = byMonth[m] || (byMonth[m] = { month: m, dau: 0, days: 0, mau: 0, installs: 0 });
      e.dau += a.dau; e.days += 1; e.mau = a.mau; e.installs += a.installs;
    });
    lines.push(section(
      'Per month (the current month is partial)',
      table(['Month', 'Avg DAU', 'MAU (month end)', 'Installs'], Object.values(byMonth).slice(-12).map(e => [e.month, n(e.dau / e.days), n(e.mau), n(e.installs)])),
      'Last 12 months.',
    ));
  }

  if (players) {
    const dp = players.dailyPlayers || [];
    const games = data.games || Object.keys(dp[dp.length - 1] || {}).filter(k => !['date', 'players'].includes(k));
    lines.push(section(
      'Players per game',
      `Registered users: ${n(players.users)} (${n(players.usersWithHistory)} with play history).\n\n` +
        table(['Period', 'Players', ...games.map(gameName)], [7, 30].map(d => {
          const rows = dp.slice(-d);
          return [`Avg per day, last ${d} days`, n(sum(rows, 'players') / (rows.length || 1)), ...games.map(g => n(sum(rows, g) / (rows.length || 1)))];
        })),
    ));
    if (players.habit) {
      const h = players.habit;
      lines.push(section(
        `Habit (${h.from} – ${h.to}, ${n(h.players)} players)`,
        'Days played:\n\n' + table(['Days played', 'Users'], (h.daysPlayed || []).map(b => [b.label, n(b.users)])) +
          '\nGames per player:\n\n' + table(['Games', 'Users'], (h.gamesPerPlayer || []).map(b => [b.label, n(b.users)])) +
          (h.gameReach ? '\nPlayers who played each game in this period:\n\n' + table(['Game', 'Players', 'Share of players'], Object.entries(h.gameReach).sort((a, b) => b[1] - a[1]).map(([g, v]) => [gameName(g), n(v), share(v, h.players)])) : ''),
      ));
    }
    if (players.retention?.cohorts?.length) {
      lines.push(section(
        'Retention',
        table(
          ['Week of signup', 'Users', ...players.retention.windows.map(w => w.label)],
          players.retention.cohorts.slice(-12).map(c => [c.week, n(c.users), ...players.retention.windows.map(w => (c.rates?.[w.key] == null ? '–' : pc(c.rates[w.key])))]),
        ),
        'Share of each weekly signup cohort that played in each window. "–" means the window has not happened yet. Last 12 cohorts; all in the JSON under `players.retention.cohorts`.',
      ));
    }
    if (players.streaks) {
      const s = players.streaks;
      lines.push(section('Streaks', bullets([`Alive: ${n(s.alive)}, 7+ days: ${n(s.atLeast7)}, 30+ days: ${n(s.atLeast30)}, longest: ${n(s.longest)}`]) + '\n' + table(['Length', 'Users'], (s.buckets || []).map(b => [b.label, n(b.users)]))));
    }
    if (players.onboarding) {
      lines.push(section(
        'Onboarding answers (all users)',
        'How they found the app:\n\n' + answers(players.onboarding.trafficSource, 'Source') + '\nCrossword experience:\n\n' + answers(players.onboarding.experience, 'Experience'),
      ));
    }
  }

  if (data.puzzles) {
    // Today's puzzles have barely been started, so they are left out
    const complete = (rows) => (rows || []).filter(r => !data.today || r.date < data.today);
    const crosswords = complete(data.puzzles.crosswords);
    const teralehti = complete(data.puzzles.teralehti);
    if (crosswords.length) {
      const recent = crosswords.slice(-14);
      lines.push(section(
        'Crosswords: starters and solvers per day',
        table(
          ['Date', 'Words', 'Starters', 'Solvers', 'Solve rate', 'Median solve time', 'Solved last (avg position)'],
          recent.map(c => [c.date, n(c.words), n(c.starters), n(c.solvers), share(c.solvers, c.starters), c.medianSolveSeconds == null ? '–' : `${n(c.medianSolveSeconds / 60, 1)} min`, c.lastSolved ? `${c.lastSolved.answer} (${n(c.lastSolved.avgPosition, 1)})` : '–']),
        ) +
          `\nAverage over all ${crosswords.length} days: ${n(sum(crosswords, 'starters') / crosswords.length)} starters, ${n(sum(crosswords, 'solvers') / crosswords.length)} solvers (${share(sum(crosswords, 'solvers'), sum(crosswords, 'starters'))}).\n`,
        'Last 14 complete days (today is left out); all days in the JSON under `puzzles.crosswords`. Median solve time is for signed-in solvers; "solved last" is the word solvers complete last on average.',
      ));
    }
    if (teralehti.length) {
      lines.push(section(
        'Terälehti: players and words found per day',
        table(['Date', 'Words', 'Players', 'Avg share of words found', 'Least found'], teralehti.slice(-14).map(t => [t.date, n(t.words), n(t.players), pc(t.avgFoundShare), (t.leastFound || []).join(', ') || '–'])),
        'Last 14 days; all days in the JSON under `puzzles.teralehti`.',
      ));
    }
  }

  if (events) {
    lines.push(section(
      `Event window: ${events.since} – ${events.until}`,
      bullets([
        `Active users in window: ${n(events.activeUsers)}`,
        events.onboarding && `Onboarding: first open ${n(events.onboarding.firstOpen)}, started ${n(events.onboarding.started)}, completed ${n(events.onboarding.completed)} (${share(events.onboarding.completed, events.onboarding.started)} of started)`,
        events.onboarding?.notifications && `Notification permission: asked ${n(events.onboarding.notifications.asked)}, granted ${n(events.onboarding.notifications.granted)} (${share(events.onboarding.notifications.granted, events.onboarding.notifications.asked)}); streak-reminder prompt: asked ${n(events.onboarding.streakNotifications?.asked)}, granted ${n(events.onboarding.streakNotifications?.granted)}`,
        events.notifications && `Notifications: received ${n(events.notifications.received)}, opened ${n(events.notifications.opened)} (${share(events.notifications.opened, events.notifications.received)}), dismissed ${n(events.notifications.dismissed)}`,
        events.hints && `Hints used: crossword ${n(events.hints.crossword?.events)} times by ${n(events.hints.crossword?.users)} users, sudoku ${n(events.hints.sudoku?.events)} times by ${n(events.hints.sudoku?.users)} users`,
        events.rewardedAds && `Rewarded ads watched: ${n(events.rewardedAds.events)} times by ${n(events.rewardedAds.users)} users`,
        events.teralehtiCapHits && `Terälehti daily limit reached: ${n(events.teralehtiCapHits.events)} times by ${n(events.teralehtiCapHits.users)} users`,
        events.exceptions && `App exceptions: ${n(events.exceptions.events)} by ${n(events.exceptions.users)} users`,
      ]),
    ));
    if (events.onboarding) {
      const o = events.onboarding;
      lines.push(section(
        'Onboarding answers in the event window',
        'Sign-in method:\n\n' + answers(o.method, 'Method') + '\nHow they found the app:\n\n' + answers(o.discovery, 'Source') + '\nMotivation:\n\n' + answers(o.motivation, 'Motivation') + '\nCrossword experience:\n\n' + answers(o.experience, 'Experience'),
      ));
    }
    if (events.games?.length) {
      lines.push(section(
        'Game funnels (event window)',
        table(['Game', 'Users who started', 'Users who finished', 'Finish rate (users)', 'Games started', 'Games finished', 'Shares'], events.games.map(g => [gameName(g.game), n(g.startedUsers), n(g.finishedUsers), g.finishedUsers == null ? '–' : share(g.finishedUsers, g.startedUsers), n(g.started), n(g.finished), n(g.shared)])),
        'Terälehti has no finish event.',
      ));
    }
    const dists = [];
    if (events.wordleGuesses?.length) dists.push('Wordle, guesses to solve (by users):\n\n' + answers(events.wordleGuesses, 'Guesses'));
    if (events.quordleWins?.length) dists.push('Neluilu, win vs loss (by users):\n\n' + answers(events.quordleWins.map(r => ({ ...r, value: r.value === 'true' ? 'win' : 'loss' })), 'Result'));
    if (events.teralehtiRanks?.length) dists.push('Terälehti, rank reached (by users):\n\n' + answers(events.teralehtiRanks, 'Rank'));
    if (events.sudokuDifficulty?.started?.length) {
      const fin = Object.fromEntries((events.sudokuDifficulty.finished || []).map(r => [r.value, r.users]));
      dists.push('Sudoku by difficulty:\n\n' + table(['Difficulty', 'Users who started', 'Users who finished', 'Finish rate'], events.sudokuDifficulty.started.map(r => [r.value, n(r.users), n(fin[r.value]), share(fin[r.value], r.users)])));
    }
    (events.crossword || []).forEach(c => {
      dists.push(`Crossword ${c.isDaily ? 'daily puzzles' : 'archive puzzles'}: ${n(c.completions)} completions, ${pc(c.noHintShare)} without hints, ${n(c.avgHints, 1)} hints on average.`);
    });
    if (dists.length) lines.push(section('Game details (event window)', dists.join('\n\n') + '\n'));
    if (events.paywall?.length) {
      lines.push(section('Paywall by source', table(['Source', 'Viewed', 'Purchase attempts', 'Purchased'], events.paywall.map(p => [p.source, n(p.viewed), n(p.purchase_attempt), n(p.purchased)]))));
    }
    if (events.activation?.length) {
      lines.push(section(
        'Activation by install day (last 14)',
        table(['Date', 'Installs', 'Onboarded', 'Played', 'Finished', 'Saw offer', 'Back day 1', 'Back week 1'], events.activation.slice(-14).map(a => [a.date, n(a.installs), n(a.onboarded), n(a.played), n(a.finished), n(a.sawOffer), n(a.d1), n(a.w1)])),
        'The newest rows are incomplete: "Back day 1" needs a full day after install and "Back week 1" a full week, so they undercount recent installs.',
      ));
    }
    if (events.devices?.length) {
      lines.push(section('Platforms and versions (top 12)', table(['Platform', 'Version', 'Users'], [...events.devices].sort((a, b) => b.users - a.users).slice(0, 12).map(d => [d.platform, d.version, n(d.users)]))));
    }
    if (events.heatmap?.length) {
      const hours = Array(24).fill(0);
      events.heatmap.forEach(h => { hours[h.hour] += h.events; });
      const total = hours.reduce((s, v) => s + v, 0);
      lines.push(section('Play time by hour (share of events, all weekdays)', table(['Hour', 'Share'], hours.map((v, h) => [`${String(h).padStart(2, '0')}:00`, share(v, total)])), 'Hours are in the timezone of the BigQuery export; per weekday data is in the JSON under `events.heatmap`.'));
    }
  }
  return lines.join('\n');
}
